/**
 * stock.ts
 * Compras (REQUI) module — Stock section: warehouses (pañol) admin, stock
 * movement types, consumption/dispatch/adjustment/cyclic-count writes, and
 * the on-hand/movement read-side pages.
 *
 * refactor/compras-by-section — pure move out of index.ts (no shape/behavior change).
 */
import { z } from 'zod';

// Warehouses (compras.warehouses, migration 0156) — REQUI inventory/stock
// (pañol) manual admin ABM, requi-inventario-stock PR 1. Uniqueness is on
// (company_id, lower(name)) — `code` stays nullable and unconstrained
// (design.md: "uniqueness is on name, code is optional and unconstrained").
// ---------------------------------------------------------------------------

export const WarehouseCreateSchema = z.object({
  name: z.string().min(1, 'name is required'),
  code: z.string().max(64, 'code must be at most 64 characters').optional(),
});
export type WarehouseCreate = z.infer<typeof WarehouseCreateSchema>;

/** Every field editable except id/companyId/active (active toggles only via the deactivate route). */
export const WarehouseUpdateSchema = WarehouseCreateSchema.partial();
export type WarehouseUpdate = z.infer<typeof WarehouseUpdateSchema>;

export const WarehouseResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  code: z.string().nullable(),
  name: z.string(),
  active: z.boolean(),
  createdAt: z.string(),
});
export type WarehouseResponse = z.infer<typeof WarehouseResponseSchema>;

// ---------------------------------------------------------------------------
// Stock engine (compras.stock_balances / stock_movements, migration 0156) —
// requi-inventario-stock PR 2. Mirrors the domain enum
// (packages/modules/compras/src/domain/apply-stock-movement.ts's
// STOCK_MOVEMENT_TYPES) so frontend and backend share one closed vocabulary,
// same convention as ITEM_KINDS/MODALIDADES/DEMAND_ORIGINS.
// ---------------------------------------------------------------------------

export const STOCK_MOVEMENT_TYPES = [
  'ingreso_recepcion_oc',
  'egreso_consumo',
  'ajuste_manual_positivo',
  'ajuste_manual_negativo',
  'transferencia_entrada', // reserved — no v1 use case (design.md decision 2)
  'transferencia_salida', // reserved — no v1 use case (design.md decision 2)
  'conteo_ciclico', // migration 0157 — cyclic count, qty = 0 when no difference is found
  'devolucion_campo', // odd/tasks/compras-deposito-mover.md S2 — return from the field against a delivery line
] as const;
export const StockMovementTypeSchema = z.enum(STOCK_MOVEMENT_TYPES);
export type StockMovementType = z.infer<typeof StockMovementTypeSchema>;

/**
 * REQ-55 — routine consumption. `note` optional (design.md's two-conventions note).
 *
 * `caseId`/`caseItemId` — requi-inventario-stock PR 3: when this consumption
 * is imputed to a purchase-case line (e.g. "Abastecer desde pañol",
 * SupplyFromWarehouseUseCase), both travel together so the resulting
 * `egreso_consumo` movement carries the same traceability
 * `ingreso_recepcion_oc` already gets from RegisterReceptionUseCase. Absent =
 * free consumption, exactly today's behavior (no case attribution).
 */
export const StockConsumptionSchema = z.object({
  warehouseId: z.string().uuid(),
  itemMasterId: z.string().uuid(),
  qty: z.number().positive(),
  note: z.string().min(1).optional(),
  caseId: z.string().uuid().optional(),
  caseItemId: z.string().uuid().optional(),
});
export type StockConsumption = z.infer<typeof StockConsumptionSchema>;

/**
 * 2026-09-01-locx-deposito-materiales B1 — the remito de salida (design.md
 * D6/D7/D9/D10). Structural mirror of `CreateStockDispatchInput`
 * (`CreateStockDispatchUseCase.ts` / `IStockDispatchPort.ts`) — compras' own
 * canonical validator for the dispatch write, independent of the LOCX-facing
 * `DepositoDispatchCreateSchema` in `@suite/contracts-servicios` (no
 * cross-package import between the two contracts packages; same posture as
 * `DepositoPoLineSchema` mirroring `ReceivablePoLineView` without importing
 * from compras).
 */
export const StockDispatchLineSchema = z.object({
  itemMasterId: z.string().uuid(),
  qty: z.number().positive(),
});
export type StockDispatchLine = z.infer<typeof StockDispatchLineSchema>;

export const StockDispatchCreateSchema = z.object({
  warehouseId: z.string().uuid(),
  /** ISO-8601. Backdating allowed, future rejected — enforced by the use case (D10), not a schema-level CHECK. */
  dispatchedAt: z.string(),
  deliveredToName: z.string().trim().min(1),
  destinationServiceOrderId: z.string().uuid().optional(),
  destinationNote: z.string().optional(),
  lines: z.array(StockDispatchLineSchema).min(1),
  fileIds: z.array(z.string().uuid()).optional(),
});
export type StockDispatchCreate = z.infer<typeof StockDispatchCreateSchema>;

/** REQ-54 — manual adjustment. `note` REQUIRED (an adjustment always states why). */
export const StockAdjustmentSchema = z.object({
  warehouseId: z.string().uuid(),
  itemMasterId: z.string().uuid(),
  qty: z.number().positive(),
  direction: z.enum(['positivo', 'negativo']),
  note: z.string().min(1, 'note is required for a manual adjustment'),
});
export type StockAdjustment = z.infer<typeof StockAdjustmentSchema>;

/**
 * Migration 0157 — cyclic count (conteo cíclico). `countedQty` is the
 * PHYSICAL count the pañol just took (absolute, not a delta). `note` is
 * OPTIONAL at the schema level — it is REQUIRED only when the count finds a
 * difference against the current balance, a cross-field rule that (same
 * posture as CompraDirectaJustificadaEventSchema/ActualizarOrdenCompraEventSchema)
 * a flat ZodObject can't express; RegisterStockCountUseCase enforces it.
 */
export const StockCountSchema = z.object({
  warehouseId: z.string().uuid(),
  itemMasterId: z.string().uuid(),
  countedQty: z.number().nonnegative(),
  note: z.string().min(1).optional(),
});
export type StockCount = z.infer<typeof StockCountSchema>;

/**
 * odd/tasks/compras-deposito-conteo.md S3 — POST /procurement/stock/counts/batch.
 * Many physical counts of ONE warehouse applied in one transaction
 * (all-or-nothing), each through the same cyclic count as StockCountSchema.
 * `note` is optional: the use case writes "Conteo" when a line differs and no
 * note was sent. One line per article, at most STOCK_COUNT_BATCH_MAX_LINES.
 */
/** Each line is ~4 statements in one tx; 2000 keeps a batch well inside request and statement timeouts. */
export const STOCK_COUNT_BATCH_MAX_LINES = 2000;

export const StockCountBatchLineSchema = z.object({
  itemMasterId: z.string().uuid(),
  countedQty: z.number().nonnegative(),
});
export type StockCountBatchLine = z.infer<typeof StockCountBatchLineSchema>;

export const StockCountBatchSchema = z
  .object({
    warehouseId: z.string().uuid(),
    note: z.string().trim().min(1).max(200).optional(),
    lines: z.array(StockCountBatchLineSchema).min(1, 'at least one line is required').max(STOCK_COUNT_BATCH_MAX_LINES),
  })
  .refine((b) => new Set(b.lines.map((l) => l.itemMasterId)).size === b.lines.length, {
    message: 'each article may appear only once',
    path: ['lines'],
  });
export type StockCountBatch = z.infer<typeof StockCountBatchSchema>;

export const StockCountBatchResponseSchema = z.object({
  warehouseId: z.string().uuid(),
  /** Lines applied (every line of the batch). */
  counted: z.number(),
  /** Lines whose count differed from the balance and moved it. */
  adjusted: z.number(),
});
export type StockCountBatchResponse = z.infer<typeof StockCountBatchResponseSchema>;

/**
 * GET /procurement/stock/count-sheet?warehouseId= — every active stockable
 * article of the catalog with its balance in that warehouse. `qty` is null
 * when the article never had a movement there (balances are created lazily),
 * which is what the initial load counts.
 */
export const StockCountSheetRowSchema = z.object({
  itemMasterId: z.string().uuid(),
  code: z.string().nullable(),
  description: z.string(),
  unit: z.string(),
  qty: z.string().nullable(),
  lastVerifiedAt: z.string().nullable(),
});
export type StockCountSheetRow = z.infer<typeof StockCountSheetRowSchema>;

export const StockCountSheetResponseSchema = z.object({
  warehouseId: z.string().uuid(),
  items: z.array(StockCountSheetRowSchema),
});
export type StockCountSheetResponse = z.infer<typeof StockCountSheetResponseSchema>;

/** REQ-59.1 — GET /procurement/stock/balances row. */
/**
 * `lastVerifiedAt`/`mostRecentMovementAt` (locx-stock-inventario) — data-age
 * fields backing both the pañol's own "última verificación" and the LOCX
 * read-only stock port. `lastVerifiedAt` is migration 0157's cyclic-count
 * stamp (null = never counted); `mostRecentMovementAt` is the fallback used
 * when a position has ledger activity but was never physically counted.
 */
export const StockOnHandRowSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  itemMasterId: z.string().uuid(),
  code: z.string().nullable(),
  description: z.string(),
  unit: z.string(),
  qty: z.string(),
  lastVerifiedAt: z.string().nullable(),
  mostRecentMovementAt: z.string().nullable(),
  /** Migration 0050 (odd/tasks/compras-deposito-minimos.md S1) — null = not set. */
  minQty: z.string().nullable().optional(),
  maxQty: z.string().nullable().optional(),
});
export type StockOnHandRow = z.infer<typeof StockOnHandRowSchema>;

export const StockOnHandPageSchema = z.object({
  items: z.array(StockOnHandRowSchema),
  totalCount: z.number(),
  totalPages: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type StockOnHandPage = z.infer<typeof StockOnHandPageSchema>;

/** REQ-59.2 — GET /procurement/stock/movements row. */
export const StockMovementRowSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  itemMasterId: z.string().uuid(),
  type: StockMovementTypeSchema,
  qty: z.string(),
  balanceAfter: z.string(),
  caseId: z.string().uuid().nullable(),
  caseItemId: z.string().uuid().nullable(),
  note: z.string().nullable(),
  actorId: z.string().uuid(),
  createdAt: z.string(),
});
export type StockMovementRow = z.infer<typeof StockMovementRowSchema>;

export const StockMovementPageSchema = z.object({
  items: z.array(StockMovementRowSchema),
  totalCount: z.number(),
  totalPages: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type StockMovementPage = z.infer<typeof StockMovementPageSchema>;

/**
 * 2026-09-01-locx-deposito-materiales B1c — closed vocabulary for the
 * `StockDispatchFailureCode` members that have no existing `COMPRAS_ERROR_CODES`
 * entry (`INSUFFICIENT_STOCK`/`ITEM_NOT_STOCKABLE` above already cover two of
 * the seven; `WAREHOUSE_NOT_FOUND`/`ITEM_NOT_FOUND` map to the kernel's
 * generic `NOT_FOUND`). Consumed by `locx-deposito.routes.ts`'s dispatch
 * error mapping (design D8) — never thrown/instanceof-checked, only used as
 * the envelope's `code` string.
 */
export const STOCK_DISPATCH_ERROR_CODES = {
  NON_POSITIVE_QTY: 'COMPRAS_STOCK_DISPATCH_NON_POSITIVE_QTY',
  DUPLICATE_ITEM_LINE: 'COMPRAS_STOCK_DISPATCH_DUPLICATE_ITEM_LINE',
  EMPTY_DISPATCH: 'COMPRAS_STOCK_DISPATCH_EMPTY_DISPATCH',
} as const;

/**
 * odd/tasks/compras-deposito-minimos.md S1 — PUT /procurement/stock/minimums.
 * Reorder point ("mínimo") and "pedir hasta" of one article in one warehouse;
 * null clears it. A "pedir hasta" needs a minimum and is never below it (also
 * CHECKed by migration 0050).
 */
export const StockMinimumSetSchema = z
  .object({
    warehouseId: z.string().uuid(),
    itemMasterId: z.string().uuid(),
    minQty: z.number().nonnegative().nullable(),
    maxQty: z.number().nonnegative().nullable(),
  })
  .refine((b) => b.maxQty === null || (b.minQty !== null && b.maxQty >= b.minQty), {
    message: '"pedir hasta" needs a minimum and cannot be below it',
    path: ['maxQty'],
  });
export type StockMinimumSet = z.infer<typeof StockMinimumSetSchema>;

export const StockMinimumResponseSchema = z.object({
  warehouseId: z.string().uuid(),
  itemMasterId: z.string().uuid(),
  minQty: z.string().nullable(),
  maxQty: z.string().nullable(),
});
export type StockMinimumResponse = z.infer<typeof StockMinimumResponseSchema>;

/**
 * S2 — GET /procurement/stock/below-minimum?warehouseId=. Positions whose qty
 * is below their minimum. `suggestedQty` = ("pedir hasta", or 2 × mínimo when
 * unset) − qty: what "Pedir compra" requests (S3).
 */
export const StockBelowMinimumRowSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  itemMasterId: z.string().uuid(),
  code: z.string().nullable(),
  description: z.string(),
  unit: z.string(),
  categoryId: z.string().uuid(),
  qty: z.string(),
  minQty: z.string(),
  maxQty: z.string().nullable(),
  suggestedQty: z.string(),
  /** Newest open "Reposición de stock" SA holding this article; the home shows it instead of "Pedir compra". */
  openCaseId: z.string().uuid().nullable(),
  openCaseCode: z.string().nullable(),
});
export type StockBelowMinimumRow = z.infer<typeof StockBelowMinimumRowSchema>;

export const StockBelowMinimumResponseSchema = z.object({
  items: z.array(StockBelowMinimumRowSchema),
});
export type StockBelowMinimumResponse = z.infer<typeof StockBelowMinimumResponseSchema>;

/**
 * odd/tasks/compras-deposito-mover.md S1 — POST /procurement/stock/transfers.
 * Articles out of one warehouse and into another in one transaction
 * (transferencia_salida + transferencia_entrada per line). One line per article.
 */
export const StockTransferCreateSchema = z
  .object({
    fromWarehouseId: z.string().uuid(),
    toWarehouseId: z.string().uuid(),
    note: z.string().trim().min(1).max(200).optional(),
    lines: z
      .array(z.object({ itemMasterId: z.string().uuid(), qty: z.number().positive() }))
      .min(1, 'at least one line is required')
      .max(200),
  })
  .refine((b) => b.fromWarehouseId !== b.toWarehouseId, { message: 'origin and destination must differ', path: ['toWarehouseId'] })
  .refine((b) => new Set(b.lines.map((l) => l.itemMasterId)).size === b.lines.length, { message: 'each article may appear only once', path: ['lines'] });
export type StockTransferCreate = z.infer<typeof StockTransferCreateSchema>;

export const StockTransferResponseSchema = z.object({
  fromWarehouseId: z.string().uuid(),
  toWarehouseId: z.string().uuid(),
  lines: z.array(z.object({ itemMasterId: z.string().uuid(), qty: z.string(), fromBalanceAfter: z.string(), toBalanceAfter: z.string() })),
});
export type StockTransferResponse = z.infer<typeof StockTransferResponseSchema>;

/**
 * odd/tasks/compras-deposito-mover.md S2 — POST /procurement/stock/dispatch-returns.
 * What came back unused from a delivery line, named by that line's own movement
 * (what the Movimientos ledger shows). Never more than delivered minus returned.
 */
export const StockDispatchReturnCreateSchema = z.object({
  stockMovementId: z.string().uuid(),
  qty: z.number().positive(),
  note: z.string().trim().min(1).max(200).optional(),
});
export type StockDispatchReturnCreate = z.infer<typeof StockDispatchReturnCreateSchema>;

export const StockDispatchReturnResponseSchema = z.object({
  dispatchId: z.string().uuid(),
  dispatchCode: z.string(),
  itemMasterId: z.string().uuid(),
  qty: z.string(),
  balanceAfter: z.string(),
  returnableAfter: z.string(),
});
export type StockDispatchReturnResponse = z.infer<typeof StockDispatchReturnResponseSchema>;

/**
 * odd/tasks/operator-panol.md S1/S2 — GET /procurement/stock/scan?code=.
 * A scanned code → its article (by the article's own code or a learned
 * barcode) with its stock per warehouse; `article: null` when RigX does not
 * know the code yet.
 */
export const StockScanArticleSchema = z.object({
  itemMasterId: z.string().uuid(),
  code: z.string().nullable(),
  description: z.string(),
  unit: z.string(),
});
export type StockScanArticle = z.infer<typeof StockScanArticleSchema>;

export const StockScanResponseSchema = z.object({
  code: z.string(),
  matchedBy: z.enum(['code', 'barcode']).nullable(),
  article: StockScanArticleSchema.nullable(),
  balances: z.array(z.object({ warehouseId: z.string().uuid(), warehouseName: z.string(), qty: z.string() })),
});
export type StockScanResponse = z.infer<typeof StockScanResponseSchema>;

/** POST /procurement/stock/barcodes — learn an unknown code for an article (one article per code). */
export const StockBarcodeLinkSchema = z.object({
  code: z.string().trim().min(1).max(64),
  itemMasterId: z.string().uuid(),
});
export type StockBarcodeLink = z.infer<typeof StockBarcodeLinkSchema>;
